import { useEffect } from 'react';

// The on-screen keyboard.
//
// Android Chrome shrinks the layout viewport for us once the viewport meta
// says interactive-widget=resizes-content (public/index.html), so 100dvh
// follows the keyboard there. iOS Safari never resizes the layout viewport:
// it shrinks only the VISUAL viewport and scrolls the page to bring the field
// into view, which drags fixed bars off the top and leaves whatever sat at
// the bottom under the keys.
//
// So on phones the app shell follows the visual viewport on every platform:
//   --app-height  its height (the shell's height on immersive routes)
//   --app-top     how far the page has been scrolled under it (the shell and
//                 the fixed nav bar move down by this much, so they stay
//                 where the eye is)
//   body.kb-open  a text field has focus and the viewport lost at least
//                 120px — the keyboard is up. The phone layout hides its tab
//                 bar and segment switch then, to make room.
const TEXT_TAGS = /^(INPUT|TEXTAREA|SELECT)$/;
const KEYBOARD_MIN_PX = 120;

export const isTyping = (doc = document) => {
  const el = doc.activeElement;
  return Boolean(el) && (TEXT_TAGS.test(el.tagName) || el.isContentEditable === true);
};

// Exported for the test; the hook below is what the app uses.
export function attachKeyboardViewport(win = window) {
  const vv = win.visualViewport;
  const doc = win.document;
  if (!vv || !doc) return () => {};
  const root = doc.documentElement;
  let tallest = vv.height;

  const apply = () => {
    tallest = Math.max(tallest, vv.height);
    root.style.setProperty('--app-height', `${Math.round(vv.height)}px`);
    root.style.setProperty('--app-top', `${Math.round(vv.offsetTop || 0)}px`);
    const open = isTyping(doc) && vv.height < tallest - KEYBOARD_MIN_PX;
    doc.body.classList.toggle('kb-open', open);
    // The newest line of the conversation stays above the keys.
    if (open) doc.querySelector('.ds-chat-screen .ag-chat-body')?.scrollTo?.({ top: 1e9 });
  };
  const applySoon = () => win.setTimeout(apply, 60);
  // A rotation changes what "tallest" means.
  const onOrientation = () => { tallest = 0; applySoon(); };

  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  doc.addEventListener('focusin', apply);
  doc.addEventListener('focusout', applySoon);
  win.addEventListener('orientationchange', onOrientation);
  apply();

  return () => {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    doc.removeEventListener('focusin', apply);
    doc.removeEventListener('focusout', applySoon);
    win.removeEventListener('orientationchange', onOrientation);
    root.style.removeProperty('--app-height');
    root.style.removeProperty('--app-top');
    doc.body.classList.remove('kb-open');
  };
}

export default function useKeyboardViewport(enabled) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    return attachKeyboardViewport(window);
  }, [enabled]);
}
